import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { workspaceLogin } from '@/services/api/auth.service'
import { ApiError } from '@/services/api/client'
import { getSubscriptionStatus } from '@/services/api/subscription.service'
import { getWorkspaces } from '@/services/api/workspace.service'
import { useAuthStore } from '@/stores/auth.store'
import { usePermissionStore } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { logger } from '@/utils/logger'

interface Workspace {
  id: string
  name: string
}

function findCurrentWorkspace(workspaces: Workspace[], currentId: string | null): Workspace | undefined {
  return workspaces.find((workspace) => workspace.id === currentId)
}

export function useWorkspaceSwitcher() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const workspaceStore = useWorkspaceStore()
  const authStore = useAuthStore()
  const permissionStore = usePermissionStore()
  const subscriptionStore = useSubscriptionStore()

  const [isSwitching, setIsSwitching] = useState(false)
  const { setWorkspaces } = authStore
  const { currentWorkspaceId, currentWorkspaceName, setWorkspace } = workspaceStore

  const {
    data: workspaces = [],
    isLoading,
    isError,
    isSuccess,
    refetch,
  } = useQuery<Workspace[]>({
    queryKey: ['workspaces', authStore.userId],
    queryFn: getWorkspaces,
    enabled: !!authStore.userId,
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (isSuccess) {
      setWorkspaces(workspaces)
    }

    if (!isSuccess || isLoading || workspaces.length === 0) {
      return
    }

    const current = findCurrentWorkspace(workspaces, currentWorkspaceId)

    if (!current) {
      const firstWorkspace = workspaces[0]

      setWorkspace(firstWorkspace.id, firstWorkspace.name)
    } else if (
      current.id !== currentWorkspaceId ||
      current.name !== currentWorkspaceName
    ) {
      // A session restored from a refresh only knows the workspace id.
      setWorkspace(current.id, current.name)
    }
  }, [
    isLoading,
    isSuccess,
    workspaces,
    setWorkspaces,
    currentWorkspaceId,
    currentWorkspaceName,
    setWorkspace,
  ])
  
  const selectedWorkspace =
    findCurrentWorkspace(workspaces, workspaceStore.currentWorkspaceId) ?? workspaces[0]

  const currentWorkspace = {
    id: selectedWorkspace?.id ?? null,
    name: selectedWorkspace?.name ?? '',
  }

  const handleAuthFailure = (message: string) => {
    toast.error(message)

    workspaceStore.clearWorkspace()
    permissionStore.clear()
    subscriptionStore.clear()
    authStore.logout()

    queryClient.clear()
    navigate('/login')
  }

  const switchWorkspace = async (
    workspaceId: string
  ): Promise<boolean> => {
    const targetWorkspace = workspaces.find(
      (workspace) => workspace.id === workspaceId
    )

    if (!targetWorkspace) {
      toast.error('Workspace not found.')
      return false
    }

    if (isSwitching) {
      return false
    }

    if (
      targetWorkspace.id ===
      workspaceStore.currentWorkspaceId
    ) {
      return true
    }

    setIsSwitching(true)

    try {
      const userId = authStore.userId

      if (!userId) {
        handleAuthFailure(
          'Unable to identify user session.'
        )

        return false
      }

      /*
       * Login into the selected workspace.
       */
      const response = await workspaceLogin({
        userId,
        workspaceId: targetWorkspace.id,
      })

      /*
       * Leave the current page and drop the previous workspace's cached data before the new
       * token applies. Many queries are not keyed by workspace (e.g. a record), so a page left
       * mounted would refetch the old workspace's resources with the new token — the backend
       * rejects that (403) and the user would see an error toast.
       */
      navigate('/')
      await queryClient.cancelQueries()
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== 'workspaces',
      })

      /*
       * Update authentication state.
       */
      authStore.setAccessToken(response.accessToken)
      authStore.setUserId(userId)

      /*
       * Update current workspace.
       */
      workspaceStore.setWorkspace(
        targetWorkspace.id,
        targetWorkspace.name
      )

      /*
       * Update permissions.
       */
      permissionStore.setIsSuperUser(
        response.superUser
      )

      permissionStore.setIsDeveloper(
        response.developerRole
      )

      // Effective permissions reload via usePermission() (query keyed by workspace).

      /*
       * Update subscription.
       */
      try {
        const subscription =
          await getSubscriptionStatus()

        subscriptionStore.setStatus(
          subscription.status
        )

        subscriptionStore.setCancelAtPeriodEnd(
          subscription.cancelAtPeriodEnd
        )
      } catch (error) {
        logger.warn('[Workspace Switch] Subscription fetch failed', error)
      }

      /*
       * Keep the already fetched workspace list.
       */
      queryClient.setQueryData(
        ['workspaces', userId],
        workspaces
      )

      return true
    } catch (error: unknown) {
      // Reported to the user below; 'expected' keeps it out of Sentry, as before.
      logger.error('[Workspace Switch Error]', error, 'expected')

      if (
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403)
      ) {
        handleAuthFailure(
          'Session invalidated. Please sign in again.'
        )
      } else {
        toast.error(
          'Failed to switch workspace. Please try again.'
        )
      }

      return false
    } finally {
      setIsSwitching(false)
    }
  }

  return {
    currentWorkspace,
    workspaces,
    isLoading: isLoading || isSwitching,
    isError,
    refetchWorkspaces: refetch,
    switchWorkspace,
  }
}
